import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import api, { normalize } from '../services/api';
import CadastroDataTable from '../components/CadastroDataTable/CadastroDataTable';
import './EmpresaPage.css';
import './AgentesPage.css';

const TIPOS = [
  { label: 'Texto', value: 'Texto' },
  { label: 'Numero', value: 'Numero' },
  { label: 'Lista', value: 'Lista' },
  { label: 'Data', value: 'Data' },
  { label: 'Booleano', value: 'Booleano' },
  { label: 'Moeda', value: 'Moeda' },
];

const EMPTY = {
  id: null,
  descricao: '',
  tipo: 'Texto',
  tipolista: '',
  obrigatorio: false,
  status: 'Ativo',
  grupo: '',
  ordemExibicao: 0,
  exibirNaEmissao: true,
  exibirNaConsulta: true,
  incluirNoModeloExcel: false,
};

export default function CaracteristicasPage({ embedded = false }) {
  const toast = useRef(null);
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [salvando, setSalvando] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/caracteristicas-nfse');
      setItens(normalize(data));
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar características.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const setCampo = (c, v) => setForm((p) => ({ ...p, [c]: v }));

  const salvar = async () => {
    if (!form.descricao?.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Atenção', detail: 'Informe a descrição.' });
      return;
    }
    setSalvando(true);
    try {
      const payload = { ...form };
      if (form.id) await api.put(`/caracteristicas-nfse/${form.id}`, payload);
      else await api.post('/caracteristicas-nfse', payload);
      toast.current?.show({ severity: 'success', summary: 'Salvo' });
      setVisible(false);
      carregar();
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: e.response?.data?.message || 'Falha ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const excluir = (row) => {
    confirmDialog({
      message: `Remover "${row.descricao}"?`,
      header: 'Confirmação',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: async () => {
        await api.delete(`/caracteristicas-nfse/${row.id}`);
        carregar();
      },
    });
  };

  const columns = useMemo(
    () => [
      { field: 'ordemExibicao', header: 'Ordem', style: { width: 80 } },
      { field: 'descricao', header: 'Descrição' },
      { field: 'tipo', header: 'Tipo' },
      { field: 'tipolista', header: 'Tipo lista', defaultVisible: false },
      { field: 'grupo', header: 'Grupo' },
      { field: 'status', header: 'Status', defaultVisible: false },
      { field: 'obrigatorio', header: 'Obrig.', body: (r) => (r.obrigatorio ? 'Sim' : 'Não'), exportValue: (r) => (r.obrigatorio ? 'Sim' : 'Não') },
      { field: 'exibirNaEmissao', header: 'Emissão', body: (r) => (r.exibirNaEmissao ? 'Sim' : 'Não'), exportValue: (r) => (r.exibirNaEmissao ? 'Sim' : 'Não') },
      { field: 'incluirNoModeloExcel', header: 'Excel', body: (r) => (r.incluirNoModeloExcel ? 'Sim' : 'Não'), exportValue: (r) => (r.incluirNoModeloExcel ? 'Sim' : 'Não') },
      {
        field: '_acoes',
        header: 'Ações',
        sortable: false,
        alwaysVisible: true,
        body: (row) => (
          <div className="agentes-acoes">
            <Button icon="pi pi-pencil" rounded text onClick={() => { setForm({ ...EMPTY, ...row }); setVisible(true); }} />
            <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => excluir(row)} />
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="agentes-page">
      <Toast ref={toast} />
      <ConfirmDialog acceptLabel="Sim" rejectLabel="Não" />
      <div className="agentes-header">
        {!embedded && <h1>Características NFS-e</h1>}
        {embedded && <h2 style={{ margin: 0, color: '#002478', fontSize: '1.15rem' }}>Características NFS-e</h2>}
        <Button label="Nova característica" icon="pi pi-plus" onClick={() => { setForm(EMPTY); setVisible(true); }} />
      </div>

      <CadastroDataTable
        value={itens}
        columns={columns}
        storageKey="caracteristicas"
        loading={loading}
        title="Características NFS-e"
        emptyMessage="Nenhuma característica."
        globalFilterFields={['descricao', 'tipo', 'grupo']}
      />

      <Dialog header={form.id ? 'Editar' : 'Nova'} visible={visible} style={{ width: 720 }} breakpoints={{ '960px': '95vw' }} onHide={() => setVisible(false)}>
        <div className="agentes-form">
          <div className="nfse-form-row">
            <div className="agentes-field agentes-field-full">
              <label>Descrição</label>
              <InputText value={form.descricao} onChange={(e) => setCampo('descricao', e.target.value)} />
            </div>
          </div>
          <div className="nfse-form-row">
            <div className="agentes-field">
              <label>Tipo</label>
              <Dropdown value={form.tipo} options={TIPOS} onChange={(e) => setCampo('tipo', e.value)} />
            </div>
            <div className="agentes-field agentes-field-sm">
              <label>Ordem</label>
              <InputNumber value={form.ordemExibicao} onValueChange={(e) => setCampo('ordemExibicao', e.value ?? 0)} />
            </div>
            <div className="agentes-field">
              <label>Grupo</label>
              <InputText value={form.grupo} onChange={(e) => setCampo('grupo', e.target.value)} />
            </div>
          </div>
          {form.tipo === 'Lista' && (
            <div className="nfse-form-row">
              <div className="agentes-field agentes-field-full">
                <label>Opções da lista (separadas por vírgula)</label>
                <InputText value={form.tipolista} onChange={(e) => setCampo('tipolista', e.target.value)} />
              </div>
            </div>
          )}
          <div className="nfse-form-row">
            <div className="agentes-field agentes-field-checkbox">
              <Checkbox checked={form.obrigatorio} onChange={(e) => setCampo('obrigatorio', e.checked)} inputId="ob" />
              <label htmlFor="ob">Obrigatório</label>
            </div>
            <div className="agentes-field agentes-field-checkbox">
              <Checkbox checked={form.exibirNaEmissao} onChange={(e) => setCampo('exibirNaEmissao', e.checked)} inputId="em" />
              <label htmlFor="em">Exibir na emissão</label>
            </div>
          </div>
          <div className="nfse-form-row">
            <div className="agentes-field agentes-field-checkbox">
              <Checkbox checked={form.exibirNaConsulta} onChange={(e) => setCampo('exibirNaConsulta', e.checked)} inputId="co" />
              <label htmlFor="co">Exibir na consulta</label>
            </div>
            <div className="agentes-field agentes-field-checkbox">
              <Checkbox checked={form.incluirNoModeloExcel} onChange={(e) => setCampo('incluirNoModeloExcel', e.checked)} inputId="ex" />
              <label htmlFor="ex">Incluir no modelo Excel</label>
            </div>
          </div>
        </div>
        <div className="agentes-dialog-footer">
          <Button label="Cancelar" className="p-button-text" onClick={() => setVisible(false)} />
          <Button label="Salvar" icon="pi pi-save" loading={salvando} onClick={salvar} />
        </div>
      </Dialog>
    </div>
  );
}
